import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { api } from "../lib/api";
import SiteFooter from "../components/SiteFooter";
import BrandLogo from "../components/BrandLogo";
import BrandSocialProof from "../components/forbrands/BrandSocialProof";
import DemoModal from "../components/forbrands/DemoModal";
import { ArrowRight, Search, MessageSquare, Shield, Trophy, Check, Calendar, Building2 } from "lucide-react";

// Same hero background that Landing uses, so the two surfaces feel visually
// continuous when a visitor toggles between them.
const HERO_BG = "https://images.unsplash.com/photo-1767028071770-e775364a93ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGRhcmslMjBzcG9ydHMlMjBiYWNrZ3JvdW5kJTIwc3RhZGl1bXxlbnwwfHx8fDE3Nzg3MTY0MzB8MA&ixlib=rb-4.1.0&q=85";

function Header({ onBookDemo, onSignup }) {
  return (
    <header className="fixed top-0 w-full z-50 bg-bg/85 backdrop-blur-xl border-b border-line">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex justify-between items-center gap-3">
        <Link to="/" className="flex items-center flex-shrink-0" data-testid="for-brands-logo">
          <BrandLogo variant="wordmark" size={28} showLink={false} />
        </Link>
        <nav className="hidden md:flex items-center gap-8 text-xs uppercase tracking-widest text-ink-muted">
          <a href="#how" className="hover:text-white transition-colors" data-testid="link-brand-how">How it works</a>
          <a href="#pricing" className="hover:text-white transition-colors" data-testid="link-brand-pricing">Pricing</a>
          <Link to="/" className="hover:text-volt transition-colors" data-testid="link-for-athletes">For athletes</Link>
        </nav>
        <div className="flex items-center gap-2.5 sm:gap-3">
          <Link to="/login" className="text-[10px] sm:text-xs uppercase tracking-widest text-ink-muted hover:text-white whitespace-nowrap" data-testid="link-login">Sign in</Link>
          <button onClick={onBookDemo} className="btn-ghost-line text-xs hidden md:inline-flex items-center gap-1.5"
                  data-testid="nav-cta-demo">
            <Calendar className="w-3.5 h-3.5" /> Book a demo
          </button>
          <button onClick={onSignup} className="btn-volt text-[10px] sm:text-xs whitespace-nowrap" data-testid="hero-cta-signup">
            <span className="hidden sm:inline">Get brand access</span>
            <span className="sm:hidden">Get access</span>
          </button>
        </div>
      </div>
    </header>
  );
}

export default function ForBrands() {
  const navigate = useNavigate();
  const [previewAthletes, setPreviewAthletes] = useState([]);
  const [directoryStats, setDirectoryStats] = useState({ total: 0 });
  const [demoOpen, setDemoOpen] = useState(false);

  useEffect(() => {
    // Try to fetch a preview slice (will fail anonymously by design — we only show a teaser).
    api.get("/directory/athletes?limit=8").then(({ data }) => {
      setPreviewAthletes(data.items?.slice(0, 6) || []);
      setDirectoryStats({ total: data.total || 0 });
    }).catch(() => {});
  }, []);

  const goSignup = () => navigate("/register?as=brand");

  return (
    <div className="min-h-screen bg-bg text-white">
      <Header onBookDemo={() => setDemoOpen(true)} onSignup={goSignup} />

      {/* Hero — mirrors Landing's hero: fixed header offset, bg image with
          gradient overlay, max-w-7xl container, 12-col grid for the headline
          stack. Layout reads as a sibling page to Landing, not a different app. */}
      <section className="relative pt-32 pb-24 md:pt-40 md:pb-32 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-bg via-bg/60 to-bg" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <div className="label-eyebrow text-volt mb-6">For brands &amp; sponsors</div>
          <h1 className="font-heading text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.95] uppercase font-bold tracking-tighter max-w-5xl" data-testid="for-brands-hero-headline">
            The fastest way<br />
            to find, evaluate,<br />
            and manage<br />
            <span className="text-volt">athlete partnerships.</span>
          </h1>
          <p className="text-ink-muted text-lg md:text-xl mt-8 max-w-2xl leading-relaxed">
            PITCHD is the partnership infrastructure for brands serious about sports. Discover sponsor-ready athletes, evaluate fit with AI, and run every collaboration through a single workflow. Built for brand managers who&apos;d rather ship campaigns than sift through DMs.
          </p>
          <div className="flex flex-wrap gap-3 mt-10">
            <button onClick={goSignup} className="btn-volt inline-flex items-center gap-2" data-testid="for-brands-register-cta">
              Open brand workspace <ArrowRight className="w-4 h-4" />
            </button>
            <button onClick={() => setDemoOpen(true)} className="btn-ghost-line inline-flex items-center gap-2" data-testid="hero-cta-demo">
              <Calendar className="w-4 h-4" /> Book a 15-min demo
            </button>
            <a href="#how" className="btn-ghost-line inline-flex items-center gap-2" data-testid="hero-cta-how-it-works">How it works</a>
          </div>
          <div className="flex flex-wrap gap-8 mt-12 text-xs uppercase tracking-widest text-ink-muted">
            {/* Only show the count when we have a real, non-fabricated number from the API.
                Falls back to a capability claim ("Open directory") to avoid inventing metrics. */}
            {directoryStats.total > 0 ? (
              <div><span className="text-volt font-mono text-2xl block">{directoryStats.total}</span>Sponsor-ready athletes</div>
            ) : (
              <div><span className="text-volt font-mono text-2xl block">Open</span>Athlete directory</div>
            )}
            <div><span className="text-volt font-mono text-2xl block">1</span>Workspace, end to end</div>
            <div><span className="text-volt font-mono text-2xl block">AI</span>Campaign-fit matching</div>
          </div>
        </div>
      </section>

      {/* Athlete preview strip */}
      <section className="py-24 border-t border-line">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="label-eyebrow">Inside the directory</div>
          <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mt-4 max-w-3xl">A glimpse at sponsor-ready athletes.</h2>
          <div className="mt-12">
            {previewAthletes.length > 0 ? (
              <div className="grid md:grid-cols-3 gap-4">
                {previewAthletes.map(a => (
                  <div key={a.athlete_user_id} className="surface p-5" data-testid={`preview-athlete-${a.athlete_user_id}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="font-heading text-lg uppercase tracking-tight">{a.full_name}</div>
                        <div className="text-xs text-ink-muted mt-1">{a.sport}{a.level && ` · ${a.level}`}</div>
                      </div>
                      {a.positioning_score !== null && a.positioning_score !== undefined && (
                        <div className="text-volt font-mono text-2xl">{a.positioning_score}</div>
                      )}
                    </div>
                    <div className="text-xs text-ink-muted mt-3">{a.location}</div>
                  </div>
                ))}
              </div>
            ) : (
              // Empty-state: directory hasn't been populated yet (or API unreachable).
              // Show a visual illustration of what the grid looks like, but with
              // NO fake metrics, NO fake locations, NO fake scores — just structural
              // placeholders so brand visitors can imagine the surface.
              <div data-testid="forbrands-empty-directory">
                <div className="surface p-8 text-center">
                  <div className="label-eyebrow text-volt">Directory opening soon</div>
                  <h3 className="font-heading text-xl md:text-2xl uppercase tracking-tight mt-3 max-w-xl mx-auto">
                    The athlete directory is in early onboarding.
                  </h3>
                  <p className="text-sm text-ink-muted mt-3 max-w-lg mx-auto leading-relaxed">
                    Athletes are joining weekly. Request a demo and we&apos;ll preview the live shortlist that matches your brief — directly, before signup.
                  </p>
                  <button onClick={() => setDemoOpen(true)} className="btn-volt mt-6 inline-flex items-center gap-2" data-testid="empty-directory-demo-btn">
                    Request a preview demo
                  </button>
                </div>
              </div>
            )}
            <div className="mt-10">
              <button onClick={goSignup} className="btn-volt inline-flex items-center gap-2 text-sm" data-testid="preview-cta">
                Unlock the full directory <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="py-24 border-t border-line">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="label-eyebrow">How partnerships flow</div>
          <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mt-4 max-w-3xl">From discovery to deal — without leaving PITCHD.</h2>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            <Step n="01" icon={Search} title="Discover" desc="Filter the directory by sport, region, audience and positioning score. Only opt-in athletes appear." />
            <Step n="02" icon={MessageSquare} title="Start partnership" desc="Open a conversation with a clear subject, budget indication and campaign window. No emails revealed." />
            <Step n="03" icon={Shield} title="Run the deal in-app" desc="Negotiate, accept, decline, send updates. PITCHD owns the audit trail — clean, professional, premium." />
          </div>
        </div>
      </section>

      {/* Why we're different */}
      <section id="results" className="py-24 border-t border-line">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="label-eyebrow">Why brands ship faster on PITCHD</div>
          <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mt-4 max-w-3xl">Built for campaigns, not directories.</h2>
          <div className="grid md:grid-cols-2 gap-6 mt-12">
            <Diff title="AI-ranked athlete fit, not endless filtering" desc="Describe your campaign in plain English. Our AI ranks the network by fit score with reasoning — so you start with a shortlist that already makes sense, not a 10,000-row spreadsheet." />
            <Diff title="Positioning Score on every athlete" desc="Each athlete profile carries an AI-generated sponsorship-readiness score (0-100). Skip the dud profiles. Focus on the ones built to deliver." />
            <Diff title="One workspace for the whole campaign" desc="Outreach, replies, accept/decline decisions, contract notes and analytics — all on one timeline. No more chasing conversations across DMs, email and Notion." />
            <Diff title="Sponsorship intelligence, not vanity metrics" desc="Reply rates, response time, deliverable history, audience demographics. Real signals that predict whether a partnership will actually ship." />
          </div>
        </div>
      </section>

      {/* Brand social proof — case studies + testimonials */}
      <BrandSocialProof />

      {/* Pricing — Brand plans */}
      <section id="pricing" className="py-24 border-t border-line">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="label-eyebrow">Brand pricing</div>
          <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mt-4 max-w-3xl" data-testid="for-brands-pricing-heading">
            Start free. Scale into the workspace.
          </h2>
          <p className="text-ink-muted mt-4 max-w-2xl text-sm leading-relaxed">
            Set up a brand workspace at no cost. Browse athletes, save a shortlist, send your first partnership. Move up when you&apos;re running real campaigns and need the AI matcher, analytics and team collaboration.
          </p>

          <div className="grid md:grid-cols-3 gap-4 mt-12">
            {/* Brand Free — entry */}
            <BrandPlanCard
              name="Brand Free" price="£0" sub="To explore"
              feats={[
                "Save 20 athletes to your shortlist",
                "3 partnerships per month",
                "Manual outreach to athletes",
                "Single seat",
              ]}
              cta="Open brand workspace" testid="brand-pricing-free"
            />
            {/* Brand Lite — operational */}
            <BrandPlanCard
              name="Brand Lite" price="£49" sub="per month"
              feats={[
                "Save up to 100 athletes",
                "25 partnerships per month",
                "Full outreach + reply tracking",
                "Verified domain badge",
                "Email support",
              ]}
              cta="Start Brand Lite" testid="brand-pricing-lite"
            />
            {/* Brand Pro — flagship */}
            <BrandPlanCard
              name="Brand Pro" price="£299" sub="per month"
              primary
              feats={[
                "Unlimited shortlist + partnerships",
                "AI Campaign Matcher + Wizard",
                "Campaign workspace + analytics",
                "Athlete delivery + response signals",
                "Team collaboration (multi-seat)",
                "Priority support & onboarding",
              ]}
              cta="Start Brand Pro" testid="brand-pricing-pro"
            />
          </div>

          {/* For-athletes callout — mirrors Landing's For-Brands callout pattern so
              both surfaces have a single cross-promotion CTA at the same position. */}
          <Link to="/" data-testid="for-brands-athlete-callout"
            className="group mt-10 flex flex-wrap items-center justify-between gap-4 border border-line hover:border-volt transition-colors px-6 py-5 bg-bg-surface">
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-10 h-10 border border-volt/40 flex items-center justify-center flex-shrink-0">
                <Trophy className="w-4 h-4 text-volt" />
              </div>
              <div className="min-w-0">
                <div className="font-heading text-lg uppercase tracking-tight">For Athletes</div>
                <p className="text-xs text-ink-muted mt-0.5">Editorial media kits, AI outreach, sponsor pipeline — the toolkit pros pay agents for, in one workspace.</p>
              </div>
            </div>
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-volt group-hover:translate-x-0.5 transition-transform">
              See athlete plans <ArrowRight className="w-3.5 h-3.5" />
            </div>
          </Link>

          <p className="text-xs text-ink-muted mt-8 max-w-2xl">
            All plans billed monthly in GBP. Cancel anytime. Need enterprise volume, custom contract terms or procurement? <a href="mailto:partnerships@pitchdhq.com" className="text-volt hover:text-white">Talk to us</a>.
          </p>
        </div>
      </section>

      {/* CTA — centred, mirrors Landing's bottom CTA pattern */}
      <section className="py-32 border-t border-line">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-5xl md:text-6xl uppercase tracking-tighter">
            Ready to find your<br />
            <span className="text-volt">next athlete?</span>
          </h2>
          <p className="text-ink-muted mt-6 text-lg max-w-xl mx-auto">Brand workspace is free to set up. Pay only when you scale.</p>
          <div className="flex flex-wrap gap-3 mt-10 justify-center">
            <button onClick={goSignup} className="btn-volt inline-flex items-center gap-2" data-testid="footer-cta-primary">
              Get brand access <ArrowRight className="w-4 h-4" />
            </button>
            <button onClick={() => setDemoOpen(true)} className="btn-ghost-line inline-flex items-center gap-2" data-testid="footer-cta-demo">
              <Calendar className="w-4 h-4" /> Book a demo
            </button>
            <Link to="/" className="btn-ghost-line inline-flex items-center gap-2" data-testid="footer-cta-athletes">I&apos;m an athlete</Link>
          </div>
        </div>
      </section>

      <SiteFooter />
      <DemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
    </div>
  );
}

function Step({ n, icon: Icon, title, desc }) {
  return (
    <div className="surface p-6 md:p-7">
      <div className="flex items-center justify-between mb-4">
        <div className="label-eyebrow text-volt">{n}</div>
        <Icon className="w-5 h-5 text-volt" />
      </div>
      <h3 className="font-heading text-xl uppercase tracking-tight">{title}</h3>
      <p className="text-sm text-ink-muted leading-relaxed mt-2">{desc}</p>
    </div>
  );
}

function Diff({ title, desc }) {
  return (
    <div className="border-l-2 border-volt pl-6 py-2">
      <h3 className="font-heading text-xl uppercase tracking-tight">{title}</h3>
      <p className="text-sm text-ink-muted leading-relaxed mt-2">{desc}</p>
    </div>
  );
}

function BrandPlanCard({ name, price, sub, feats, cta, primary = false, testid }) {
  const navigate = useNavigate();
  return (
    <div className={`p-8 border ${primary ? "border-volt bg-bg-elev" : "border-line bg-bg-surface"} relative flex flex-col`}
         data-testid={testid}>
      {primary && (
        <div className="absolute -top-3 left-8 bg-volt text-black text-[10px] tracking-widest uppercase px-2 py-1 font-bold">
          Most popular
        </div>
      )}
      <div className="font-heading text-3xl uppercase">{name}</div>
      <div className="mt-4">
        <span className="font-heading text-5xl">{price}</span>
        <span className="text-ink-muted ml-2 text-sm">{sub}</span>
      </div>
      <ul className="mt-6 space-y-3 text-sm flex-1">
        {feats.map(f => (
          <li key={f} className="flex items-start gap-2">
            <Check className="w-4 h-4 text-volt mt-0.5 shrink-0" />{f}
          </li>
        ))}
      </ul>
      <button
        onClick={() => navigate("/register?as=brand")}
        className={`mt-8 inline-flex w-full justify-center ${primary ? "btn-volt" : "btn-ghost-line"}`}
        data-testid={`${testid}-cta`}
      >
        {cta}
      </button>
    </div>
  );
}
